import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { BehaviorSubject, switchMap, map, of, distinctUntilChanged, skip, Subject, merge, fromEvent, EMPTY, timer } from 'rxjs';
import { switchMap as switchMap$1, distinctUntilChanged as distinctUntilChanged$1 } from 'rxjs/operators';
import * as i1 from '@egovsolutions/angular-egov-api/common';

// Password validator
const PASSWORD_VALIDATOR = Validators.pattern(/(?=[\s\S]*\d)(?=[\s\S]*[a-z]).{8,}/i);
// Injection tokens
const FORCE_SHORT_SESSION_TIMES = new InjectionToken('Force short session times');

const SESSION_STORAGE_KEY = 'egov-session-info';
class EgovSessionAbstract {
    constructor(api, responses, lifetimeManager) {
        this.api = api;
        this.responses = responses;
        this.lifetimeManager = lifetimeManager;
        // Session
        this.internalSessionInfo$ = new BehaviorSubject(null);
        this.sessionInfo$ = this.internalSessionInfo$.pipe(switchMap(internal => internal
            ? (internal.type === 'anonymous' ? this.lifetimeManager.anonymousLifetime$ : this.lifetimeManager.userLifetime$).pipe(map(lifetime => ({
                ...internal,
                expiration: lifetime && internal.renewed + lifetime,
            })))
            : of(null)));
        this.expiration$ = this.sessionInfo$.pipe(map(s => s?.expiration), distinctUntilChanged());
        this.signedIn$ = this.sessionInfo$.pipe(map(session => session?.type === 'user'));
        this.init();
        this.lifetimeManager.registerSession(this);
    }
    // Init
    init() {
        this.restoreSession();
        this.interceptSession();
        this.initializeSessionStoring();
        this.trackJWT();
    }
    restoreSession() {
        const stored = localStorage.getItem(SESSION_STORAGE_KEY);
        const storedSessionInfo = stored && JSON.parse(stored) || null;
        if (storedSessionInfo && storedSessionInfo.expiration > Date.now())
            this.internalSessionInfo$.next(storedSessionInfo);
        else
            this.internalSessionInfo$.next(null);
    }
    interceptSession() {
        this.responses.responses$.subscribe(this.handleSession.bind(this));
    }
    initializeSessionStoring() {
        this.sessionInfo$.pipe(skip(1)).subscribe(sessionInfo => {
            if (sessionInfo)
                try {
                    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionInfo));
                }
                catch { }
            else
                localStorage.removeItem(SESSION_STORAGE_KEY);
        });
    }
    updateSessionInfo(newSessionData) {
        this.internalSessionInfo$.next({ ...this.internalSessionInfo$.value, ...newSessionData });
    }
    flushSession() { this.internalSessionInfo$.next(null); }
    handleSession(response) {
        if (response) {
            if (response.user) {
                this.updateSessionInfo({
                    jwt: response.user.token,
                    type: 'user',
                    renewed: Date.now(),
                    user: this.adaptSession(response),
                });
                return;
            }
            if (!this.api.bearer && response.token) {
                this.updateSessionInfo({
                    jwt: response.token,
                    type: 'anonymous',
                    renewed: Date.now(),
                    user: null,
                });
                return;
            }
            if (this.internalSessionInfo$.value && response.session)
                switch (response.session) {
                    case 'alive':
                        this.updateSessionInfo({
                            renewed: Date.now(),
                        });
                        break;
                    case 'expired':
                    case 'error':
                        this.flushSession();
                }
        }
    }
    trackJWT() {
        this.internalSessionInfo$.subscribe(session => this.api.bearer = session?.jwt || null);
    }
}

const USER_LIFETIME_STORAGE_KEY = 'egov-jwt-lifetime';
const ANONYMOUS_LIFETIME_STORAGE_KEY = 'egov-anonymous-jwt-lifetime';
class SessionLifetimeManager {
    constructor(
    // Dependencies
    ngZone, responses, forceShortSessionTimes) {
        this.ngZone = ngZone;
        this.responses = responses;
        this.forceShortSessionTimes = forceShortSessionTimes;
        // Lifetime
        this._anonymousLifetime$ = new BehaviorSubject(undefined);
        this.anonymousLifetime$ = this._anonymousLifetime$.asObservable();
        this._userLifetime$ = new BehaviorSubject(undefined);
        this.userLifetime$ = this._userLifetime$.asObservable();
        // Session
        this.session$ = new BehaviorSubject(undefined);
        this.expiration$ = (() => {
            const behaviorSubject = new BehaviorSubject(undefined);
            this.session$.pipe(switchMap$1(session => session
                ? session.expiration$
                : of(undefined)), distinctUntilChanged$1()).subscribe(behaviorSubject);
            return behaviorSubject;
        })();
        this.sessionExpired = new Subject();
        // Durations
        this.MIN_TIME_BETWEEN_RENEWALS = this.forceShortSessionTimes
            ? 15 * 1000
            : 60 * 1000;
        // We're killing the session a little bit earlier to avoid
        // creating the false expectation that the session is still
        // alive due to server response delays:
        this.EARLY_EXPIRY = 5 * 1000;
        // Signed in
        this.hasUserSession$ = (() => {
            const behaviorSubject = new BehaviorSubject(undefined);
            this.session$.pipe(switchMap$1(session => session
                ? session.signedIn$
                : of(undefined)), distinctUntilChanged$1()).subscribe(behaviorSubject);
            return behaviorSubject;
        })();
        // User input tracking
        this.userInput$ = merge(fromEvent(window, 'mousedown'), fromEvent(window, 'mouseup'), fromEvent(window, 'click'), fromEvent(window, 'mousemove'), fromEvent(window, 'scroll'), fromEvent(window, 'keypress'), fromEvent(window, 'DOMMouseScroll'), fromEvent(window, 'mousewheel'), fromEvent(window, 'touchmove'), fromEvent(window, 'touchend'));
        // Keep Alive
        this.sendingKeepAlive = false;
        this.init();
    }
    // Initialize
    init() {
        this.restoreLifetimeValues();
        this.interceptLifetimeValues();
        this.hasUserSession$.subscribe(hasUserSession => {
            if (hasUserSession)
                this.startTrackingUserInput();
            else
                this.stopTrackingUserInput();
        });
        const sessionUpdated$ = this.session$.pipe(switchMap$1(session => session
            ? session.sessionInfo$
            : EMPTY), distinctUntilChanged$1((old, nu) => old?.jwt === nu?.jwt));
        sessionUpdated$.subscribe(() => this.sendKeepAliveIfNecessary());
        this.trackSessionExpiration();
    }
    get userLifetime() { return this._userLifetime$.value; }
    restoreLifetimeValues() {
        const userLifetime = localStorage.getItem(USER_LIFETIME_STORAGE_KEY);
        if (userLifetime)
            this._userLifetime$.next(JSON.parse(userLifetime));
        const anonymousLifetime = localStorage.getItem(ANONYMOUS_LIFETIME_STORAGE_KEY);
        if (anonymousLifetime)
            this._anonymousLifetime$.next(JSON.parse(anonymousLifetime));
    }
    interceptLifetimeValues() {
        this.responses.responses$.subscribe(this.extractLifetimeValues.bind(this));
    }
    extractLifetimeValues(response) {
        if (response?.APP?.clientSessionLengths) {
            const lifetimes = this.adaptSessionLifetimes(response);
            this.setAnonymousLifetime(lifetimes.anonymous);
            this.setUserLifetime(lifetimes.user);
        }
    }
    adaptSessionLifetimes(response) {
        const lifetimes = { anonymous: null, user: null };
        if (!isNaN(response.APP.clientSessionLengths.timeoutMinutesAnon)
            &&
                +response.APP.clientSessionLengths.timeoutMinutesAnon > 0)
            lifetimes.anonymous = +response.APP.clientSessionLengths.timeoutMinutesAnon;
        if (!isNaN(response.APP.clientSessionLengths.timeoutMinutesUser)
            &&
                +response.APP.clientSessionLengths.timeoutMinutesUser > 0)
            lifetimes.user = +response.APP.clientSessionLengths.timeoutMinutesUser;
        return lifetimes;
    }
    setAnonymousLifetime(lifetime) {
        const time = lifetime && (this.forceShortSessionTimes ? 2 : lifetime) * 60 * 1000;
        this._anonymousLifetime$.next(time);
        try {
            localStorage.setItem(ANONYMOUS_LIFETIME_STORAGE_KEY, this._anonymousLifetime$.value + '');
        }
        catch { }
    }
    setUserLifetime(lifetime) {
        const time = lifetime && (this.forceShortSessionTimes ? 2 : lifetime) * 60 * 1000;
        this._userLifetime$.next(time);
        try {
            localStorage.setItem(USER_LIFETIME_STORAGE_KEY, this._userLifetime$.value + '');
        }
        catch { }
    }
    registerSession(session) {
        this.session$.next(session);
    }
    trackSessionExpiration() {
        this.expiration$.pipe(switchMap$1(expiration => expiration
            ? timer(new Date(expiration - this.EARLY_EXPIRY))
            : EMPTY)).subscribe(() => {
            this.sessionExpired.next();
            this.session$.value?.flushSession();
        });
    }
    startTrackingUserInput() {
        this.ngZone.runOutsideAngular(() => {
            this.userInputSubscription = this.userInput$.subscribe(() => this.sendKeepAliveIfNecessary());
        });
    }
    stopTrackingUserInput() {
        if (this.userInputSubscription) {
            this.userInputSubscription.unsubscribe();
            delete this.userInputSubscription;
        }
    }
    sendKeepAliveIfNecessary() {
        if (!this.sendingKeepAlive && this.shouldSendKeepAlive()) {
            this.ngZone.run(() => {
                this.session$.value?.keepAlive().subscribe({
                    complete: () => this.sendingKeepAlive = false,
                    error: ({ errorMessage }) => {
                        this.sendingKeepAlive = false;
                        // eslint-disable-next-line no-console
                        console.warn(`Session keepalive failed: ${errorMessage}`);
                    }
                });
            });
        }
    }
    shouldSendKeepAlive() {
        if (this.hasUserSession$.value && this.userLifetime)
            return Date.now() >= this.expiration$.value - this.userLifetime +
                this.MIN_TIME_BETWEEN_RENEWALS;
        return false;
    }
}
SessionLifetimeManager.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: SessionLifetimeManager, deps: [{ token: i0.NgZone }, { token: i1.EgovAPIResponses }, { token: FORCE_SHORT_SESSION_TIMES, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
SessionLifetimeManager.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: SessionLifetimeManager, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: SessionLifetimeManager, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i1.EgovAPIResponses }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FORCE_SHORT_SESSION_TIMES]
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { EgovSessionAbstract, FORCE_SHORT_SESSION_TIMES, PASSWORD_VALIDATOR, SessionLifetimeManager };
